import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  connectMessaging, onMessagingEvent, coeditJoin, coeditLeave, coeditOp,
  coeditCursor, coeditRequestControl, coeditSetEditor,
} from '@/lib/messagingSocket';

// Self-hosted co-editing over the messaging WS. Single-writer model: only the
// user holding edit control broadcasts snapshots (so there are no merge
// conflicts); control can be requested, granted, or taken. Shared cursors ride
// the same room. All context/state only — no source data leaves the client.
export function useCoEdit({ room, me, snapshot, applySnapshot }) {
  const [members, setMembers] = useState([]);
  const [editor, setEditor] = useState(null);
  const [cursors, setCursors] = useState({});
  const applyingRemote = useRef(false);
  const lastCursor = useRef(0);
  const canvasRef = useRef(null);
  const amEditor = !room || !editor || editor === me;

  useEffect(() => {
    if (!room) return;
    connectMessaging();
    coeditJoin(room);
    return () => coeditLeave(room);
  }, [room]);

  useEffect(() => {
    if (!room) return;
    const off = onMessagingEvent((evt) => {
      if (evt.room_id !== room) return;
      if (evt.type === 'coedit_state') { setMembers(evt.members || []); setEditor(evt.editor); }
      else if (evt.type === 'coedit_op') {
        applyingRemote.current = true;
        applySnapshot(evt.snapshot || {});
        setTimeout(() => { applyingRemote.current = false; }, 0);
      } else if (evt.type === 'coedit_cursor') {
        setCursors(c => ({ ...c, [evt.user_id]: { name: evt.name, x: evt.x, y: evt.y, ts: Date.now() } }));
      } else if (evt.type === 'coedit_control_request') {
        toast(`${evt.from_name} requests edit control`, {
          action: { label: 'Give control', onClick: () => coeditSetEditor(room, evt.from_id) },
        });
      }
    });
    return () => off();
  }, [room, applySnapshot]);

  // Only the editor broadcasts state changes — and ONLY when other people are
  // actually in the room. Broadcasting/serializing is skipped entirely when
  // you're editing solo (the common case), so typing a name no longer runs
  // JSON.stringify(entireBuilderState) on every keystroke. The snapshot is held
  // in a ref (cheap per render) and diffed inside a throttled timer, never in
  // the render body.
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const lastSentRef = useRef('');
  const hasPeers = members.some((m) => ((m && (m.user_id ?? m.id)) ?? m) !== me);
  useEffect(() => {
    if (!room || !hasPeers) return;
    if (editor && editor !== me) return;
    // Force the first tick after (re)entering a peers-present room to broadcast
    // the current snapshot, even if it's byte-identical to the last one sent
    // before we were briefly solo — so a newly joined peer always gets state.
    lastSentRef.current = '';
    const iv = setInterval(() => {
      if (applyingRemote.current) return;
      let key;
      try { key = JSON.stringify(snapshotRef.current); } catch (_) { return; }
      if (key === lastSentRef.current) return;
      lastSentRef.current = key;
      coeditOp(room, snapshotRef.current);
    }, 300);
    return () => clearInterval(iv);
  }, [room, editor, me, hasPeers]);

  // Drop cursors that stopped moving.
  useEffect(() => {
    const iv = setInterval(() => setCursors(c => {
      const now = Date.now(); const out = {}; let changed = false;
      for (const k in c) { if (now - c[k].ts < 5000) out[k] = c[k]; else changed = true; }
      return changed ? out : c;
    }), 2000);
    return () => clearInterval(iv);
  }, []);

  const onCanvasMouseMove = useCallback((e) => {
    if (!room) return;
    const now = Date.now();
    if (now - lastCursor.current < 60) return;
    lastCursor.current = now;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    coeditCursor(room, Math.round(((e.clientX - rect.left) / rect.width) * 1000),
      Math.round(((e.clientY - rect.top) / rect.height) * 1000));
  }, [room]);

  const requestControl = useCallback(() => { if (room) coeditRequestControl(room); }, [room]);
  const takeControl = useCallback(() => { if (room && me) coeditSetEditor(room, me); }, [room, me]);
  const giveControl = useCallback((uid) => { if (room) coeditSetEditor(room, uid); }, [room]);

  return { members, editor, amEditor, cursors, canvasRef, onCanvasMouseMove, requestControl, takeControl, giveControl };
}
